<template>
  <div name="timelineview">
    <innertopbar @close="closeWindow()">
      <template v-slot:breadcrumb>
        <router-link to="/reports/report-timeline"> Timeline </router-link>
        <i class="carret fas fa-caret-right"></i> {{ title }}
      </template>

      <template v-slot:listitems>
        <li class="inline-block">
          <button
            @click="showReportCard = !showReportCard"
            type="button"
            class="filter-bar-button hover:opacity-75"
          >
            {{reportCardTitle}}
          </button>
        </li>
        <li class="inline-block">
          <dropdown :options="calendarDropdown" :selected="selectedCalendar" v-on:updateOption="updateCalendarYear"
             class="w-72 bg-action-blue text-center bg-opacity-75 text-white"
             style="min-width: 20em;">
          ></dropdown>
        </li>
      </template>
    </innertopbar>

    <header
      class="w-95p mx-auto mt-6 h-12"
      v-if="!loading && timeline.length > 0"
    >
      <ul class="right">
        <template >
          <li
            v-for="(item, index) in types"
            :key="index"
            class="inline-block"
          >
            <button
              type="button"
              @click="filterValue(item.value)"
              class="filter-bar-button hover:opacity-75 focus:outline-none"
              v-bind:class="
                inFilter(item.value)
                  ? 'bg-gray-400'
                  : 'bg-white'
              "
            >
              <i
                class="far"
                v-bind:class="
                  inFilter(item.value) ? 'fa-check-square' : 'fa-square'
                "
              ></i>
               {{ item.title }} only
            </button>
          </li>
        </template>
      </ul>
      
    </header>

    <div
      style="width: 100%; text-align: center"
      class="spin-holder mt-4"
      v-if="loading"
    >
      <i class="spinner fa fa-spinner fa-spin"></i>
      <p>Loading Archive</p>
    </div>

    <div
      class="w-full text-center mt-20 pl-10 text-base"
      v-if="!loading && filteredTimeline.length <= 0"
    >
      The current selection has no timeline data available. Please select
      a different pupil or filter criteria.
    </div>

    <div> 
      <ReportCard v-show="showReportCard" :pupil="pupil" :calendar="selectedCalendar" v-if="selectedCalendar != undefined"></ReportCard>

    </div>

    <!-- component -->
    <div class="container grid grid-cols-1 w-full mx-auto mb-10 align-top items-start">
      <div class="w-full pb-4" v-for="(row, index) in filteredTimeline" :key="index">
        <h2
          class="
            text-center
            p-2
            bg-gray-700
            text-white text-lg
            font-bold
            w-48
            rounded
            mx-auto
            mt-0
            mb-2
          "
        >
          {{ row.month }}
        </h2>
        <div
          v-for="(item, index) in row.results"
          :key="item.identifier"
          class="flex flex-col md:grid grid-cols-9 mx-auto text-blue-50"
        >
          <template v-if="index % 2 != 0">
            <!-- left -->
            <div class="flex flex-row-reverse md:contents">
              <div
                class="
                  bg-white
                  col-start-1 col-end-5
                  p-6
                  pt-3
                  rounded
                  my-4
                  ml-auto
                  shadow
                  w-11/12
                "
              >
                <h2 class="font-bold text-xl text-gray-500">
                  {{ parseDate(item.entry_date) }}
                  <span
                    class="
                      text-gray-400 text-right
                      relative
                      capitalize
                      text-sm
                      float-right
                    "
                    >{{ item.type.title }}</span
                  >
                </h2>
                <h3 class="font-semibold text-sm mb-1">{{ item.title }}</h3>
                
             
                <div v-if="isAssessment(item)">
                  <ul>
                    <li v-for="row in criteriaList" :key="row.key" class="my-2">
                      {{ row.value }} : <span v-html="setValue(row, item)" />
                    </li>
                  </ul>
                </div>
                <div v-else
                  class="leading-tight text-justify"
                  v-html="parseContent(item)"
                ></div>
                <div class="grid grid-cols-3">
                  <evidencevideo
                    v-if="isVideo(item)"
                    reference="'ref_'+index"
                    :url="loadFile(item.media_url)"
                    width="100%"
                    height="100%"
                  ></evidencevideo>
                  <evidenceimage
                    v-if="isCamera(item)"
                    :url="loadFile(item.media_url)"
                    reference="'ref_'+index"
                  ></evidenceimage>
                </div>
              </div>
              <div class="col-start-5 col-end-6 md:mx-auto relative mr-10">
                <div class="h-full w-6 flex items-center justify-center">
                  <div class="h-full w-1 bg-gray-300 pointer-events-none"></div>
                </div>
                <div class="ball top-1" v-bind:class="item.type.class">
                  <i
                    class="fas icon"
                    v-bind:class="item.type.icon"
                  ></i>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <!-- right -->
            <div class="flex md:contents">
              <div class="col-start-5 col-end-6 mr-10 md:mx-auto relative">
                <div class="h-full w-6 flex items-center justify-center">
                  <div class="h-full w-1 bg-gray-300 pointer-events-none"></div>
                </div>
                <div class="ball top-1" v-bind:class="item.type.class">
                  <i
                    class="fas icon"
                    v-bind:class="item.type.icon"
                  ></i>
                </div>
              </div>
              <div
                class="
                  bg-white
                  col-start-6 col-end-10
                  p-6
                  pt-4
                  rounded
                  my-4
                  mr-auto
                  shadow
                  w-11/12
                "
              >
                <h2 class="font-bold text-xl text-gray-500">
                  {{ parseDate(item.entry_date) }}
                  <span
                    class="
                      text-gray-400 text-right
                      relative
                      capitalize
                      text-sm
                      float-right
                    "
                    >{{ item.type.value }}</span
                  >
                </h2>

                <h3 class="font-semibold text-sm mb-1">{{ item.title }}</h3>

                <div v-if="isAssessment(item)">
                  <ul>
                    <li v-for="row in criteriaList" :key="row.key" class="my-2">
                      {{ row.value }} : <span v-html="setValue(row, item)" />
                    </li>
                  </ul>
                </div>
                <div v-else
                  class="leading-tight text-justify"
                  v-html="parseContent(item)"
                ></div>


                
                <div class="grid grid-cols-3">
                  <evidencevideo
                    v-if="isVideo(item)"
                    reference="'ref_'+index"
                    :url="loadFile(item.media_url)"
                    width="100%"
                    height="100%"
                  ></evidencevideo>

                  <evidenceimage
                    v-if="isCamera(item)"
                    :url="loadFile(item.media_url)"
                    reference="'ref_'+index"
                  ></evidenceimage>
                </div>
              
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fileEvidenceMixin } from "@/mixins/file-EvidenceMixin.js";

import { ReportingService } from "@/services/reporting.service";

import { calendarYearList } from "@/assets/js/utils";

const ReportCard = () => import("@/components/reports/TheReportCard.vue");

const innertopbar = () => import("@/components/InnerTopBar.vue");
const evidenceimage = () => import("@/components/evidence/EvidenceImage.vue");
const evidencevideo = () => import("@/components/evidence/EvidenceVideo.vue");
const dropdown = () => import("@/components/Dropdown.vue");


import moment from "moment";
import { Datetime } from "vue-datetime";

import { mapGetters } from "vuex";

const timelineTypes = [
  {
    value: "assessment",
    title: "Assessments",
    class: "bg-red-400",
    icon: "fa-star-half-alt",
  },
  {
    value: "attendance",
    title: "Attendances",
    class: "bg-gray-500",
    icon: "fa-calendar-alt",
  },
  {
    value: "evidence",
    title: "Evidence",
    class: "bg-purple-400",
    icon: "fa-images",
  },
];

export default {
  name: "timelineview",
  mixins: [fileEvidenceMixin],
  props: {
    pupil: { type: Object, required: true },
    calendar: { type: Object, required: false },
    pathBack: { type: String, required: false }
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
      settings: "settings",
    }),
    calendarDropdown() {
      var results = calendarYearList().slice(0, 3);
      return results;
    },
    criteriaList() {
      if (this.settings.assessmentCriteriaList.length > 0) {
        return this.settings.assessmentCriteriaList.filter((c) => c.used === true );
      }
    },
    isDisabled() {
      return false;
    },
    title() {
      return this.pupil != undefined ? this.pupil.name : "";
    },
    startingPointTitle() {
      var current = moment();

      var month = current.format("MMMM");
      var year = current.format("YYYY");
      return month + " " + year;
    },
    filteredTimeline() {
      var vm = this;
      let filteredTypes = vm.filteredTimelineTypes;

      if (filteredTypes.length == 0 ) {
        return vm.timeline
      } else {
        let filteredArray = vm.timeline
        .filter((month) => month.results.some((r) => filteredTypes.includes(r.type.value)))
        .map(month => {
            return Object.assign({}, month, {results : month.results.filter(r => filteredTypes.includes(r.type.value))});
        })
        return filteredArray
      }
    },
    gradesList() {
      return this.settings.assessmentGradesList.filter((c) => c.used === true );
    },
    reportCardTitle() {
      return this.showReportCard ? "Hide Report Card" : "Show Report Card"
    }

  },
  data() {
    return {
      school: this.$store.getters.school,
      timeline: [],
      errored: false,
      loading: false,
      selectedCalendar: this.calendar,
      filteredTimelineTypes: [],
      types: timelineTypes,
      showFilter: false,
      showReportCard: true
    };
  },
  created() {
    if (this.selectedCalendar == undefined) {
      let calendarYear = calendarYearList().find((y) => y.current === true);
      this.selectedCalendar = calendarYear
    }
    
    if (this.pupil == undefined) {
      this.closeWindow();
      return;
    } else {
      this.loading = true;
      this.fetchTimeline();
      this.folder = this.wordNumericValue(this.currentUser.schoolID);
    }
  },
  mounted() {

  },
  methods: {
    fetchTimeline() {
      var before = moment().add(1, "days").format("YYYY-MM-DD");
      //console.log("after", before);

      var after = moment().subtract(4, "years").format("YYYY-MM-DD");
      //console.log("bef", after);

      ReportingService.getTimeline(this.pupil, this.selectedCalendar.value, after, before)
        .then((response) => this.populateTimeline(response.data.data))
        .catch((err) => {
          console.log("timline " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    populateTimeline(data) {
      var vm = this;

      var results = _(data)
        .groupBy((v) => moment(v.entry_date).format("MMMM YYYY"))
        .map((value, key) => ({ month: key, results: value }))
        .value();

      results.forEach((m) => {
        m.results.forEach((r) => {
          var found = timelineTypes.find((t) => t.value === r.type);
          if (found != undefined) {
            r["type"] = found;
          }
        });
      });

      //console.log("GRP", JSON.stringify(results[0]));
      this.timeline = results;
    },
    closeWindow() {
      if (this.pathBack != undefined) {
        this.$router.push({ path: this.pathBack });
      } else {
        this.$router.push({ path: "reports/report-timeline" });
      }
      window.scrollTo(0, 0);
    },
    parseDate(date) {
      return moment(date).format("DD MMMM YYYY");
    },
    loadFile(path) {
      return this.folder + "/" + path;
    },
    isVideo(item) {
      return item.media_type != undefined && item.media_type === 'video'
    },
    isCamera(item) {
      return item.media_type != undefined && item.media_type === 'camera'
    },
    isAssessment(item) {
      return item.type.value === 'assessment'
    },
    parseContent(item) {
      switch (item.type.value) {
        case "evidence":
          return "";
          break;
        default:
          if (item.content === "attended") {
            return (
              "<i class='fas fa-check text-green-500'></i> " +
              item.content.toUpperCase()
            );
          } else {
            return "<i class='fas fa-times'></i> " + item.content.toUpperCase();
          }
      }
    },
    setValue(criteria, item) {
      let vm = this
      const result = JSON.parse(item.content);
      if (result == undefined) {
           return "n/a";
        } else {

        var gradeFound = "n/a"
        switch (criteria.key) {
          case "criteria_1":
            if (result.criteria_1) {
              gradeFound = vm.gradesList.find((f) => f.score === result.criteria_1);
            }
            break;
          case "criteria_2":
            if (result.criteria_2) {
              gradeFound = vm.gradesList.find((f) => f.score === result.criteria_2);
            }
            break;
          case "criteria_3":
            if (result.criteria_3) {
              gradeFound = vm.gradesList.find((f) => f.score === result.criteria_3);
            }
            break;
          case "criteria_4":
            if (result.criteria_4) {
              gradeFound = vm.gradesList.find((f) => f.score === result.criteria_4);
            }
            break;
          case "criteria_5":
            if (result.criteria_5) {
              gradeFound = vm.gradesList.find((f) => f.score === result.criteria_5);
            }
            break;
          default:
            return "n/a";
            break;
        }
        
        return gradeFound ? '<span style="background-color: #' + gradeFound.color  + ';" class="p-1 px-2 rounded text-white">' + gradeFound.value + '</span>' : "n/a"
      }
    },
    filterValue(value) {
      if (this.filteredTimelineTypes.includes(value)) {
        this.filteredTimelineTypes.splice(
          this.filteredTimelineTypes.indexOf(value),
          1
        );
      } else {
        this.filteredTimelineTypes.push(value);
      }
    },
    inFilter(value) {
      return this.filteredTimelineTypes.includes(value);
    },
    setFilter() {
      this.showFilter = !this.showFilter;
      if (this.showFilter == false) {
        this.filteredTimelineTypes = [];
      }
    },
    updateCalendarYear(cal) {
      this.selectedCalendar = cal;
      this.fetchTimeline()
    },
  },
  components: {
    innertopbar,
    evidenceimage,
    evidencevideo,
    dropdown,
    ReportCard
  },
};
</script>


<style scoped>
.ball {
  @apply w-12  h-12  absolute  -mt-32 rounded-full shadow  -ml-3 text-center;
}

.ball > .icon {
  @apply text-lg text-white pt-4 -ml-1;
}

</style>
